import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Navigate, Link } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import ClipLoader from 'react-spinners/ClipLoader';

import { useFormatMessage, useFormatDate } from 'hooks';
import Table from 'components/Table';
import paths from 'pages/Router/paths';
import ConfirmationModal from 'components/ConfirmationModal';
import NavSearch from 'components/Layout/NavSearch';
import {
  fetchSellers,
  deleteSeller,
  clearSellersData,
} from '../../state/actions/sellers';
import { sortDate } from '../../utils';
import { Timestamp } from '@firebase/firestore';

const Sellers = () => {
  const { sellerList, isAdmin, error, loading, deleted } = useSelector(
    (state) => ({
      sellerList: state.sellers.data,
      isAdmin: state.auth.userData.isAdmin,
      error: state.sellers.error,
      loading: state.sellers.loading,
      deleted: state.sellers.deleted,
    }),
    shallowEqual,
  );
  const [deleteModal, setDeleteModal] = useState({
    sellerId: null,
    isOpen: false,
  });

  const dispatch = useDispatch();

  const [search, setSearch] = useState();

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchSellers(true));
    }

    return () => dispatch(clearSellersData());
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (deleted && !loading) {
      setDeleteModal((prevState) => ({
        sellerId: null,
        isOpen: false,
      }));
    }
  }, [deleted, loading]);

  const redirect = !isAdmin && <Navigate to={paths.ROOT} />;

  const onRemoveButtonClickHandler = (sellerId) => {
    setDeleteModal((prevState) => ({
      sellerId,
      isOpen: !prevState.isOpen,
    }));
  };

  const onCloseModalHandler = () => {
    setDeleteModal({ sellerId: null, isOpen: false });
  };

  const onDeleteLevelHandler = () => {
    dispatch(deleteSeller(deleteModal.sellerId));
  };

  const columns = [
    {
      Header: '',
      accessor: 'photoURL',
      Cell: ({ row }) => (
        <div className="image">
          <img
            src={row.original.photoURL}
            alt=""
            style={{ width: '40px' }}
            className="rounded-circle"
          />
        </div>
      ),
      disableSortBy: true,
    },
    {
      Header: useFormatMessage('Levels.name'),
      accessor: 'name',
    },

    {
      Header: 'Address',
      accessor: 'address',
    },
    {
      Header: 'Active',
      accessor: 'active',
      Cell: ({ row }) => (row.original.active ? 'Yes' : 'No'),
    },
    {
      Header: 'Created At',
      accessor: 'createdAt',
      sortType: sortDate,
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {row.original.createdAt instanceof Timestamp &&
            useFormatDate(row.original.createdAt.toDate(), {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
        </small>
      ),
    },
    {
      Header: 'Modified At',
      accessor: 'modifiedAt',
      sortType: sortDate,
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {row.original.modifiedAt instanceof Timestamp &&
            useFormatDate(row.original.modifiedAt.toDate(), {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
        </small>
      ),
    },
    {
      Header: '',
      id: 'actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div className="button-actions is-right">
          <Link
            to={`/sellers/${row.original.id}`}
            className="btn btn-sm btn-primary"
          >
            <i className="feather icon-edit" />
          </Link>

          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => onRemoveButtonClickHandler(row.original.id)}
          >
            <i className="feather icon-trash" />
          </button>
        </div>
      ),
      disableSortBy: true,
    },
  ];

  const data = search
    ? sellerList.filter((el) => {
        const clonedElem = { name: el.name, address: el.address };
        return Object.values(clonedElem).some((field) =>
          field.toString().toLowerCase().includes(search.toLowerCase()),
        );
      })
    : sellerList;

  const deleteMessage = useFormatMessage('Levels.delete');

  const confirmMessage = useFormatMessage('Levels.confirm');

  const permDeleteMessage = useFormatMessage('Levels.permDelete');

  const cancelMessage = useFormatMessage('Levels.cancel');

  return (
    <>
      {redirect}
      {deleteModal.isOpen && (
        <ConfirmationModal
          isActive={deleteModal.isOpen}
          isLoading={loading}
          confirmButtonMessage={deleteMessage}
          title={confirmMessage}
          body={permDeleteMessage}
          cancelButtonMessage={cancelMessage}
          onConfirmation={onDeleteLevelHandler}
          onCancel={onCloseModalHandler}
        />
      )}
      <Card>
        <Card.Header>
          <div className="d-flex flex-row align-items-center">
            <Card.Title as="h5">Manage sellers </Card.Title>
            <NavSearch handleChange={setSearch} />
            <Link to={paths.ADD_SELLER} className="btn btn-primary ms-auto">
              Add new Seller
            </Link>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="b-table">
            {loading ? <ClipLoader /> : <Table columns={columns} data={data} />}
            {error && 'Show error'}
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

export default Sellers;
